/**
 * Aula invertida (M5) — mutaciones local-first del plan de clase.
 *
 * Escriben en PowerSync → write-path (`db/sync.rs::session_module`), que aplica
 * la authz (manager de la cohorte: ManageAcademics con scoping de sede o profesor
 * lead), el gate de la feature `academy.flipped_sessions` y la integridad DURA
 * (la sesión existe; el módulo pertenece al CURSO de la cohorte). Los
 * denormalizados cohort_id/organization_id/campus_id son autoritativos del
 * servidor (aquí se rellenan para el optimismo local).
 */

import { eq, type PowerSyncDrizzleDB, session_module } from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateUUID } from "../../utils/uuid";
import { isPlanPhase, type PlanPhase } from "./types";

export interface SessionPlanContext {
  sessionId: string;
  cohortId: string;
  organizationId: string;
  campusId?: string | null;
}

/** Añade un módulo al plan de una sesión bajo una fase. Devuelve el id creado. */
export async function addSessionModuleMutation(
  db: PowerSyncDrizzleDB,
  ctx: SessionPlanContext,
  params: { moduleId: string; phase: PlanPhase; position?: number; note?: string | null },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  const config = params.note ? JSON.stringify({ note: params.note }) : "{}";
  await db.insert(session_module).values({
    id,
    class_session_id: ctx.sessionId,
    module_id: params.moduleId,
    cohort_id: ctx.cohortId,
    organization_id: ctx.organizationId,
    campus_id: ctx.campusId ?? null,
    phase: params.phase,
    position: params.position ?? 0,
    config,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Cambia la fase y/o posición de un ítem del plan. */
export async function setSessionModulePhaseMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  phase: PlanPhase,
  position?: number,
): Promise<void> {
  if (!isPlanPhase(phase)) {
    return;
  }
  const patch: Record<string, unknown> = { phase, updated_at: nowISO() };
  if (typeof position === "number") {
    patch.position = position;
  }
  await db.update(session_module).set(patch).where(eq(session_module.id, id));
}

/** Reordena un ítem dentro de su fase. */
export async function moveSessionModuleMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  position: number,
): Promise<void> {
  await db
    .update(session_module)
    .set({ position, updated_at: nowISO() })
    .where(eq(session_module.id, id));
}

/** Quita un módulo del plan (soft-delete; restaurable con "Deshacer"). */
export async function removeSessionModuleMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  const now = nowISO();
  await db
    .update(session_module)
    .set({ deleted_at: now, updated_at: now })
    .where(eq(session_module.id, id));
}

/** Restaura un ítem quitado (deleted_at → NULL). Write-path re-valida la authz. */
export async function restoreSessionModuleMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db
    .update(session_module)
    .set({ deleted_at: null, updated_at: nowISO() })
    .where(eq(session_module.id, id));
}
